import { AxiosError, AxiosInstance, AxiosResponse } from "axios";
import { store, AppDispatch } from "../../redux/store";
import { adaptTokenRefresh, adaptLogoutAll } from "../../redux/authAdapter";
import { ApiError, ApiErrorType, classifyError, getErrorMessage, getUserErrorMessage } from "../apiErrors";
import { RefreshTokenResponse, RetryableRequest } from "../types";
import { getDeviceId, setAccessToken } from "../token";
import { setRefreshingLock, isRefreshingInAnyTab } from "../tokenRefreshLock";
import { showError } from "../../utils/alert";
import {
    isTokenRefreshing,
    setTokenRefreshing,
    queueRequest,
    processQueue,
} from "../requestQueue";

const AUTH_ENDPOINTS = ["/user-token/refresh", "/user-token/logout-current-device"];

const isAuthEndpoint = (url?: string): boolean =>
    AUTH_ENDPOINTS.some((endpoint) => url?.includes(endpoint));
export const responseInterceptor = (
    response: AxiosResponse
): AxiosResponse => {
    return response;
};

export const createResponseErrorInterceptor = (api: AxiosInstance) => {
    return async (error: AxiosError): Promise<never> => {
        const originalRequest = error.config as RetryableRequest;

        if (!error.response) {
            return Promise.reject(
                new ApiError(
                    error.code === "ECONNABORTED" ? ApiErrorType.TIMEOUT : ApiErrorType.NETWORK,
                    undefined,
                    error,
                    error.message || "Network error. Please check your connection."
                )
            );
        }

        const status = error.response.status;
        const errorMessage = getErrorMessage(error);

        if (isAuthEndpoint(originalRequest.url)) {
            const errorType = classifyError(status);
            return Promise.reject(
                new ApiError(errorType, status, error, errorMessage)
            );
        }

        if (status === 403) {
            const userMessage = getUserErrorMessage(
                new ApiError(ApiErrorType.FORBIDDEN, 403, error, errorMessage)
            );
            showError(userMessage);
            return Promise.reject(
                new ApiError(ApiErrorType.FORBIDDEN, 403, error, userMessage)
            );
        }

        if (status === 401 && !originalRequest._retry) {
            return handleTokenRefresh(originalRequest);
        }

        const errorType = classifyError(status);
        return Promise.reject(
            new ApiError(errorType, status, error, errorMessage)
        );
    };
    async function handleTokenRefresh(
        originalRequest: RetryableRequest
    ): Promise<never> {
        if (isTokenRefreshing() || isRefreshingInAnyTab()) {
            try {
                const newToken = await queueRequest();
                if (originalRequest.headers) {
                    originalRequest.headers.Authorization = newToken;
                }
                return api(originalRequest) as Promise<never>;
            } catch (queueError) {
                return Promise.reject(
                    new ApiError(
                        ApiErrorType.UNAUTHORIZED,
                        401,
                        queueError,
                        "Authentication failed - please login again"
                    )
                );
            }
        }

        originalRequest._retry = true;
        setTokenRefreshing(true);
        setRefreshingLock(true);

        try {
            const { data } = await api.post<RefreshTokenResponse>(
                "/user-token/refresh",
                {
                    deviceId: getDeviceId()
                }
            );

            const VALID_ROLES = ["ADMIN", "STORE", "OWNER", "ORGANIZER", "CUSTOMER", "ORGANIZATION"] as const;
            const normalizedRole = data.role?.toUpperCase();
            if (!normalizedRole || !VALID_ROLES.includes(normalizedRole as any)) {
                throw new Error(`Invalid role from server: ${data.role} - possible security issue`);
            }

            setAccessToken(data.accessToken);
            (store.dispatch as AppDispatch)(adaptTokenRefresh({
                accessToken: data.accessToken,
                role: normalizedRole as "ADMIN" | "STORE" | "OWNER" | "ORGANIZER" | "CUSTOMER",
            }));

            processQueue(null, data.accessToken);

            if (originalRequest.headers) {
                originalRequest.headers.Authorization = data.accessToken;
            }
            return api(originalRequest) as Promise<never>;
        } catch (refreshError) {
            const refreshStatus = (refreshError as AxiosError)?.response?.status;
            const refreshErrorMsg = (refreshError as AxiosError)?.response?.data as any;

            processQueue(refreshError, null);

            if (refreshStatus === 401 || refreshStatus === 403) {
                setAccessToken(null);
                (store.dispatch as AppDispatch)(adaptLogoutAll());

                return Promise.reject(
                    new ApiError(
                        ApiErrorType.UNAUTHORIZED,
                        401,
                        refreshError,
                        "Session invalid - please login again."
                    )
                );
            }

            return Promise.reject(
                new ApiError(
                    ApiErrorType.UNAUTHORIZED,
                    refreshStatus || 401,
                    refreshError,
                    `Token refresh failed: ${refreshErrorMsg?.message || "Unknown error"}. Original request will fail.`
                )
            );
        } finally {
            setTokenRefreshing(false);
            setRefreshingLock(false);
        }
    };
};
